import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  Box,
  Card,
  CardContent,
  IconButton,
  Chip,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";

const AdminBlogs = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    images: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  const fetchBlogs = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Fetching blogs...");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}achievements/`
      );
      setBlogs(res.data);
      toast.dismiss(loadingToast);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch blogs");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleAddBlog = () => {
    setIsEditMode(false);
    setFormData({
      title: "",
      description: "",
      images: null,
    });
    setPreviewImage(null);
    setFormDialogOpen(true);
  };

  const handleEditBlog = (blog) => {
    setIsEditMode(true);
    setSelectedBlog(blog);
    setFormData({
      title: blog.title,
      description: blog.description,
      images: null,
    });
    setPreviewImage(blog.images);
    setFormDialogOpen(true);
  };

  const handleViewBlog = (blog) => {
    setSelectedBlog(blog);
    setDialogOpen(true);
  };

  const handleDeleteBlog = (blog) => {
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!blogToDelete) return;

    const loadingToast = toast.loading("Deleting blog...");
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_API_URL}achievements/${blogToDelete.id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.dismiss(loadingToast);
      toast.success("Blog deleted successfully");
      setDeleteDialogOpen(false);
      setBlogToDelete(null);
      fetchBlogs();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to delete blog");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    const loadingToast = toast.loading(
      isEditMode ? "Updating blog..." : "Creating blog..."
    );
    try {
      const token = localStorage.getItem("token");
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      if (formData.images) {
        submitData.append("images", formData.images);
      }

      if (isEditMode) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}achievements/${selectedBlog.id}/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.dismiss(loadingToast);
        toast.success("Blog updated successfully");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}achievements/`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.dismiss(loadingToast);
        toast.success("Blog created successfully");
      }

      setFormDialogOpen(false);
      fetchBlogs();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        isEditMode ? "Failed to update blog" : "Failed to create blog"
      );
    }
  };

  const handleImageChange = (e) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    const file = e.target.files[0];
    if (file) {
      if (file.size > maxSize) {
        toast.error("Image size must be less than 2MB");
        return;
      }
      setFormData({ ...formData, images: file });
      const reader = new FileReader();
      reader.onload = (e) => setPreviewImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const BlogCard = ({ blog }) => (
    <Card className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
      <CardContent sx={{ padding: "16px 16px 0px 16px" }}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Typography
              variant="h6"
              className="font-semibold text-gray-800 mb-2 line-clamp-2"
            >
              {blog.title}
            </Typography>
            <Typography
              variant="body2"
              className="text-gray-600 mb-3 line-clamp-3"
            >
              {blog.description}
            </Typography>
            <div className="flex w-full items-center justify-between flex-col sm:flex-row gap-2 mt-2">
              <Typography variant="caption" className="text-gray-500">
                {formatDateTime(blog.created_at)}
              </Typography>
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewBlog(blog)}
                  className="bg-blue-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                >
                  <VisibilityIcon className="text-xs flex-shrink-0" />
                  <span className="truncate">View</span>
                </button>
                <button
                  onClick={() => handleEditBlog(blog)}
                  className="bg-green-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                >
                  <EditIcon className="text-xs flex-shrink-0" />
                  <span className="truncate">Edit</span>
                </button>
                <button
                  onClick={() => handleDeleteBlog(blog)}
                  className="bg-red-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                >
                  <DeleteIcon className="text-xs flex-shrink-0" />
                  <span className="truncate">Delete</span>
                </button>
              </div>
            </div>
          </div>
          {blog.images && (
            <div className="ml-4 flex-shrink-0">
              <Avatar
                src={blog.images}
                variant="rounded"
                className="w-16 h-16"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="w-full mx-auto bg-white rounded-lg shadow-md p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Student Achievement Blogs
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and showcase student achievements
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchBlogs}
                disabled={isLoading}
                className="bg-gray-600 hover:bg-gray-700"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddBlog}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add Blog
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : blogs.length === 0 ? (
          <Card className="bg-white rounded-lg shadow-sm">
            <CardContent className="text-center py-12">
              <ImageIcon className="text-gray-400 text-6xl mb-4" />
              <Typography variant="h6" className="text-gray-600 mb-2">
                No blogs found
              </Typography>
              <Typography variant="body2" className="text-gray-500 mb-4">
                Start by adding your first student achievement blog
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddBlog}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add First Blog
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                    <tr>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        ID
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Title
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Description
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Image
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Created On
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {blogs.map((blog) => (
                      <tr key={blog.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm text-gray-900">
                          #{blog.id}
                        </td>
                        <td className="px-4 py-4">
                          <Typography
                            variant="body2"
                            className="font-medium text-gray-900 line-clamp-2 max-w-xs"
                          >
                            {blog.title}
                          </Typography>
                        </td>
                        <td className="px-4 py-4">
                          <Typography
                            variant="body2"
                            className="text-gray-600 line-clamp-2 max-w-xs"
                          >
                            {blog.description}
                          </Typography>
                        </td>
                        <td className="px-4 py-4">
                          {blog.images ? (
                            <Avatar
                              src={blog.images}
                              variant="rounded"
                              className="w-12 h-12"
                            />
                          ) : (
                            <Chip
                              label="No Image"
                              size="small"
                              className="bg-gray-100 text-gray-600"
                            />
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {formatDateTime(blog.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleViewBlog(blog)}
                              className="bg-blue-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                            >
                              <VisibilityIcon className="text-xs flex-shrink-0" />
                              <span className="truncate">View</span>
                            </button>
                            <button
                              onClick={() => handleEditBlog(blog)}
                              className="bg-green-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                            >
                              <EditIcon className="text-xs flex-shrink-0" />
                              <span className="truncate">Edit</span>
                            </button>
                            <button
                              onClick={() => handleDeleteBlog(blog)}
                              className="bg-red-600 text-white px-2 py-1 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex items-center justify-center gap-1 min-w-0"
                            >
                              <DeleteIcon className="text-xs flex-shrink-0" />
                              <span className="truncate">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {blogs.map((blog) => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Blog Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isSmallMobile}
      >
        <DialogTitle className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          {isEditMode ? "Edit Blog" : "Add New Blog"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent className="space-y-4">
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
              multiline
              rows={4}
              variant="outlined"
            />
            <div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageChange}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<ImageIcon />}
                  className="w-full"
                >
                  {formData.images ? "Change Image" : "Upload Image"}
                  <span className="text-xs text-gray-500 ml-2">
                    (Max size: 2MB, PNG, JPEG, JPG only)
                  </span>
                </Button>
              </label>
              {previewImage && (
                <Box className="mt-2">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                </Box>
              )}
            </div>
          </DialogContent>
          <DialogActions className="p-4">
            <Button
              onClick={() => setFormDialogOpen(false)}
              className="text-gray-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isEditMode ? "Update Blog" : "Create Blog"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Blog Dialog */}
      <Dialog
        open={dialogOpen && selectedBlog}
        onClose={() => {
          setDialogOpen(false);
          setSelectedBlog(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isSmallMobile}
      >
        <DialogTitle className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <ImageIcon className="text-white text-xl" />
              </div>
              <div>
                <Typography variant="h6" className="font-semibold">
                  Blog Details
                </Typography>
                <Typography variant="caption" className="text-blue-100">
                  Student Achievement Blog
                </Typography>
              </div>
            </div>
            <IconButton
              onClick={() => {
                setDialogOpen(false);
                setSelectedBlog(null);
              }}
              sx={{ color: "white" }}
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="p-0">
          <div className="flex flex-col lg:flex-row">
            {/* Image Section */}
            {selectedBlog?.images && (
              <div className="lg:w-1/2 p-6 bg-gray-50">
                <div className="sticky top-0">
                  <Typography
                    variant="h6"
                    className="font-semibold mb-4 text-gray-800"
                  >
                    Featured Image
                  </Typography>
                  <div className="relative group">
                    <img
                      src={selectedBlog.images}
                      alt="Blog"
                      className="w-full aspect-square object-cover rounded-xl shadow-lg group-hover:shadow-xl transition-shadow duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-300 rounded-xl"></div>
                  </div>
                </div>
              </div>
            )}

            {/* Content Section */}
            <div
              className={`p-6 ${selectedBlog?.images ? "lg:w-1/2" : "w-full"}`}
            >
              <div className="space-y-6">
                {/* Title Section */}
                <div>
                  <Typography
                    variant="h4"
                    className="font-bold text-gray-900 mb-2 leading-tight"
                  >
                    {selectedBlog?.title}
                  </Typography>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Blog #{selectedBlog?.id}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>
                        {selectedBlog &&
                          formatDateTime(selectedBlog.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                <div>
                  <Typography
                    variant="h6"
                    className="font-semibold text-gray-800 mb-3"
                  >
                    Description
                  </Typography>
                  <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <Typography
                      variant="body1"
                      className="text-gray-700 leading-relaxed"
                    >
                      {selectedBlog?.description}
                    </Typography>
                  </div>
                </div>

                {/* Blog Info */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <Typography
                    variant="h6"
                    className="font-semibold text-blue-800 mb-3"
                  >
                    Blog Information
                  </Typography>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Typography
                        variant="caption"
                        className="text-blue-600 font-medium"
                      >
                        Blog ID
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        #{selectedBlog?.id}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        variant="caption"
                        className="text-blue-600 font-medium"
                      >
                        Created On
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        {selectedBlog &&
                          formatDateTime(selectedBlog.created_at)}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        variant="caption"
                        className="text-blue-600 font-medium"
                      >
                        Posted By
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        Admin (ID: {selectedBlog?.posted_by})
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        variant="caption"
                        className="text-blue-600 font-medium"
                      >
                        Has Image
                      </Typography>
                      <Typography variant="body2" className="text-gray-700">
                        {selectedBlog?.images ? (
                          <span className="text-green-600 font-medium">
                            ✓ Yes
                          </span>
                        ) : (
                          <span className="text-gray-500">✗ No</span>
                        )}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={() => {
                setDialogOpen(false);
                setSelectedBlog(null);
              }}
              variant="outlined"
              className="flex-1 sm:flex-none border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setDialogOpen(false);
                setSelectedBlog(null);
                handleEditBlog(selectedBlog);
              }}
              variant="contained"
              className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 shadow-lg"
              startIcon={<EditIcon />}
            >
              Edit Blog
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setBlogToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="bg-gradient-to-r from-red-600 to-red-700 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <DeleteIcon className="text-white text-xl" />
            </div>
            <div>
              <Typography variant="h6" className="font-semibold">
                Delete Blog
              </Typography>
              <Typography variant="caption" className="text-red-100">
                Confirm deletion
              </Typography>
            </div>
          </div>
        </DialogTitle>
        <DialogContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-full">
                <DeleteIcon className="text-red-600 text-2xl" />
              </div>
              <div className="mt-2">
                <Typography
                  variant="h6"
                  className="font-semibold text-gray-900"
                >
                  Are you sure?
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  This action cannot be undone.
                </Typography>
              </div>
            </div>

            {blogToDelete && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <Typography
                  variant="body2"
                  className="font-medium text-gray-800 mb-2"
                >
                  Blog to delete:
                </Typography>
                <Typography variant="body1" className="text-gray-700 mb-1">
                  <strong>Title:</strong> {blogToDelete.title}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  <strong>ID:</strong> #{blogToDelete.id}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  <strong>Created:</strong>{" "}
                  {formatDateTime(blogToDelete.created_at)}
                </Typography>
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <Typography variant="body2" className="text-red-700">
                <strong>Warning:</strong> This will permanently delete the blog
                and all associated data.
              </Typography>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="p-6 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setBlogToDelete(null);
              }}
              variant="outlined"
              className="flex-1 sm:flex-none border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 shadow-lg"
              startIcon={<DeleteIcon />}
            >
              Delete Blog
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminBlogs;
